/**
 * Cómo se AGRUPAN y cómo se LLAMAN los permisos en la pantalla de roles.
 *
 * ⚖️ Antes no existía: el diálogo dibujaba las dos listas del backend en el
 * orden del array, con etiquetas de un mapa aparte. Nadie obligaba a ese mapa
 * a parecerse al menú, y se desincronizó — la pantalla decía «Pricing» y
 * «IA Pricing» a cosas que en el menú se llaman Price Intelligence y
 * Asistente IA. Quien configuraba un rol no sabía qué estaba apagando.
 *
 * Acá los nombres son **los del menú real**, y el orden es el del menú. La
 * prueba de al lado falla si el backend declara una llave que no está ubicada
 * en ningún grupo, o si una quedó en dos: así una llave nueva no puede
 * aparecer suelta al final ni desaparecer en silencio.
 *
 * Esto es SÓLO presentación. Las llaves son las mismas que ya están en la base.
 */

import { MODULOS_POR_CONTRATO } from '@/features/auth/tenant-modules';

export interface ItemDePermiso {
  /** La llave tal como la guarda la base. */
  llave: string;
  /** Cómo se llama en el menú de la aplicación. */
  nombre: string;
  /** Qué gobierna, cuando el nombre no alcanza. */
  detalle?: string;
  /**
   * Otra llave que va implícita en ésta: al marcar ésta se marca también, y al
   * desmarcar aquélla se desmarca ésta. Así lo guardado dice lo mismo que el
   * servidor resuelve (en Reglas, cualquiera de escritura implica ver).
   */
  implica?: string;
}

export interface GrupoDePermisos {
  titulo: string;
  /**
   * El módulo al que pertenece el grupo entero. Si es un producto por contrato y la
   * empresa no lo tiene, el grupo no se ofrece (las claves se niegan igual en el servidor).
   */
  modulo?: string;
  /** Una aclaración del grupo entero, cuando hace falta. */
  nota?: string;
  items: ItemDePermiso[];
}

/**
 * Los módulos, en el orden en que aparecen en la barra lateral.
 *
 * 🔴 `menu:pricing` gobierna DOS lugares del menú a la vez: «Modelos», que
 * vive dentro de Cotiza, y las pantallas de Compite. Aparece **una sola vez**, y su `detalle` lo dice: una fila
 * llamada sólo «Pricing» fue lo que hizo creer que apagaba una sola cosa.
 * Partirla en dos llaves es un cambio de base, y está anotado como pendiente.
 */
export const GRUPOS_DE_MODULOS: GrupoDePermisos[] = [
  /*
    18-oct (rediseño, artefacto): los grupos llevan los nombres del menú nuevo
    —Cotiza, Compite, Comercia—. «Dashboard» es Cotiza › Visión general (Inicio
    no tiene llave: lo ve todo el mundo) y Analítica se mudó a Cotiza.
  */
  {
    titulo: 'Cotiza',
    nota: '«Modelos» también vive en Cotiza, pero su llave está más abajo, en Compite.',
    items: [
      { llave: 'menu:dashboard', nombre: 'Visión general' },
      { llave: 'menu:nueva-cotizacion', nombre: 'Nueva Cotización' },
      { llave: 'menu:quotes', nombre: 'Cotizaciones', detalle: 'El listado' },
      {
        llave: 'menu:pricer-datec',
        nombre: 'Cotizaciones Datec',
        detalle: 'El motor del Pricer Datec',
      },
      { llave: 'menu:opportunities', nombre: 'Oportunidades' },
      { llave: 'menu:pricer-solar', nombre: 'Cotizador Solar' },
      { llave: 'menu:approvals', nombre: 'Aprobaciones' },
      { llave: 'menu:contracts', nombre: 'Contratos' },
      { llave: 'menu:customers', nombre: 'Clientes' },
      { llave: 'menu:ai-intelligence', nombre: 'Inteligencia de Pricing' },
      { llave: 'menu:analytics', nombre: 'Analítica' },
    ],
  },
  {
    titulo: 'Compite',
    items: [
      {
        llave: 'menu:pricing',
        nombre: 'Monitoreo, Análisis, Productos, Competencia y Modelos',
        detalle:
          'Una sola llave para «Modelos» (que se ve en Cotiza) y para estas pantallas de Compite. Apagarla esconde todas. Reglas tiene sus propios permisos, en «Qué puede hacer».',
      },
      {
        llave: 'menu:data-import',
        nombre: 'Decisiones y Estrategia',
      },
    ],
  },
  {
    titulo: 'Comercia',
    items: [
      {
        llave: 'menu:import-tracking',
        nombre: 'Comercia completo',
        detalle:
          'Llave antigua: habilita el cotizador, las órdenes y aduana de una vez. Si configurás las tres de abajo, mandan ellas.',
      },
      { llave: 'menu:trade-cotizador', nombre: 'Cotizador y sus cotizaciones' },
      { llave: 'menu:trade-ordenes', nombre: 'Import / Export, Contenedores y KPIs' },
      {
        llave: 'menu:trade-tarifario',
        nombre: 'Tarifario',
        detalle: 'Editar tramos, tarifas, proveedores y servicios de flete',
      },
      { llave: 'menu:trade-aduana', nombre: 'Asistente Arancelario' },
    ],
  },
  {
    titulo: 'Datos Maestros',
    items: [
      {
        llave: 'menu:master-data',
        nombre: 'Materiales, Divisas, LOBs, Países, Incoterms y demás',
      },
    ],
  },
  {
    titulo: 'Integraciones',
    items: [
      {
        llave: 'menu:integrations',
        nombre: 'SAP, Tiendas Conectadas, API y llaves',
      },
    ],
  },
  {
    titulo: 'Mi empresa',
    items: [
      {
        llave: 'menu:admin',
        nombre: 'Usuarios, Roles, Equipos, Aprobaciones y Configuración',
        detalle: 'Es la administración de la empresa: dar acceso y repartir permisos.',
      },
    ],
  },
  {
    titulo: 'Otros',
    items: [
      { llave: 'menu:tutorial', nombre: 'Ayuda y Curso Interactivo' },
      { llave: 'menu:demo-store', nombre: 'Demo Store', detalle: 'Enlace externo' },
    ],
  },
];

/**
 * Las funcionalidades, agrupadas por lo que la persona hace.
 *
 * Antes eran 17 casillas seguidas en dos columnas, sin orden: «Congelar
 * cotizaciones» quedaba entre «Generar PDF» y «Ver reportes».
 */
export const GRUPOS_DE_FUNCIONALIDAD: GrupoDePermisos[] = [
  {
    titulo: 'Cotizaciones',
    items: [
      { llave: 'create_quotes', nombre: 'Crear cotizaciones' },
      { llave: 'view_own_quotes', nombre: 'Ver las propias' },
      {
        llave: 'view_all_quotes',
        nombre: 'Ver las de toda la empresa',
        detalle: 'Sin esto, cada uno ve las suyas y las que le compartieron',
      },
      { llave: 'edit_own_quotes', nombre: 'Editar las propias' },
      {
        llave: 'edit_all_quotes',
        nombre: 'Editar las de toda la empresa',
        detalle: 'Sobre las cotizaciones que ves; también permite eliminar borradores ajenos',
      },
      {
        llave: 'share_with_client',
        nombre: 'Compartir con el cliente',
        detalle: 'Link público para que el cliente la vea',
      },
      { llave: 'cancel_others_quotes', nombre: 'Cancelar las de otros' },
      { llave: 'reopen_approved_quotes', nombre: 'Reabrir las aprobadas de otros' },
      {
        llave: 'delete_others_attachments_comments',
        nombre: 'Borrar adjuntos y comentarios de otros',
      },
      {
        llave: 'freeze_quotes',
        nombre: 'Congelar',
        detalle: 'Dejarla fija para que no se recalcule',
      },
      { llave: 'generate_pdf', nombre: 'Generar el PDF' },
    ],
  },
  {
    titulo: 'Aprobaciones',
    items: [
      { llave: 'approve_quotes', nombre: 'Aprobar' },
      { llave: 'reject_quotes', nombre: 'Rechazar' },
      { llave: 'approve_own_quotes', nombre: 'Aprobar sus propias cotizaciones' },
      {
        llave: 'unblock_approval_chain',
        nombre: 'Destrabar una cadena de aprobación trabada',
        detalle: 'También deja ver las cotizaciones trabadas, aunque no sean tuyas',
      },
      {
        llave: 'view_client_budget',
        nombre: 'Ver presupuesto del cliente',
        detalle: 'El presupuesto que el cliente declaró al armar la cotización',
      },
    ],
  },
  {
    /*
      Pricer Datec: producto por contrato. El grupo sólo se ofrece si la empresa lo
      tiene (`gruposDisponibles`); las tres claves se niegan sin el módulo.
    */
    titulo: 'Pricer Datec',
    modulo: 'menu:pricer-datec',
    items: [
      {
        llave: 'configure_pricer',
        nombre: 'Configurar el Pricer',
        detalle: 'Formato del CITE y textos',
      },
      { llave: 'assign_legacy_cite', nombre: 'Asignar CITE a cotizaciones viejas' },
      {
        llave: 'view_costs',
        nombre: 'Ver costos y márgenes',
        detalle:
          'Incluye la carga masiva de ítems. Sin esta clave sólo se miran los precios: no se crea ni se edita en el Pricer',
      },
      /*
        Reabrir sin firma (16-oct): una casilla por nivel con el que se aprobó la cotización. Sobre una ajena
        hace falta además «Reabrir las aprobadas de otros». Los niveles 1 a 3 arrancan desmarcados para todos.
      */
      {
        llave: 'reopen_unsigned_auto',
        nombre: 'Reabrir sin firma: aprobadas automáticamente',
        detalle:
          'Las que se aprobaron solas, sin que nadie firmara. Sin esta casilla se pide la reapertura y la autoriza un aprobador',
      },
      {
        llave: 'reopen_unsigned_level_1',
        nombre: 'Reabrir sin firma: aprobadas en nivel 1',
        detalle: 'El primer tramo de margen: el del encargado de frente',
      },
      {
        llave: 'reopen_unsigned_level_2',
        nombre: 'Reabrir sin firma: aprobadas en nivel 2',
        detalle: 'El segundo tramo de margen',
      },
      {
        llave: 'reopen_unsigned_level_3',
        nombre: 'Reabrir sin firma: aprobadas en nivel 3',
        detalle: 'El tercer tramo de margen, el más exigente',
      },
    ],
  },
  {
    titulo: 'Datos y catálogo',
    items: [
      { llave: 'manage_customers', nombre: 'Administrar clientes' },
      { llave: 'manage_models', nombre: 'Administrar modelos de pricing' },
      { llave: 'manage_master_data', nombre: 'Administrar datos maestros' },
      { llave: 'export_data', nombre: 'Exportar datos' },
      {
        llave: 'import_data',
        nombre: 'Cargar datos por la API',
        detalle:
          'Productos, stock, ventas y precios de la competencia. Las claves de API no dependen de esta casilla',
      },
    ],
  },
  {
    titulo: 'Precios dinámicos',
    items: [
      {
        llave: 'calculate_prices',
        nombre: 'Recalcular el precio de un producto',
      },
      {
        llave: 'run_price_checks',
        nombre: 'Lanzar y cancelar chequeos de precios',
        detalle: 'Los chequeos de la competencia que se piden a mano',
      },
    ],
  },
  {
    /*
      Reglas de precio: cinco permisos, separados de la llave de menú de
      Precios Dinámicos (decisión del 26-sep, plan 4B-datos T1b). Cualquiera de
      escritura implica ver: el servidor lo resuelve así, y el diálogo marca
      «Ver» solo al marcar uno de los otros (`implica`).
    */
    titulo: 'Reglas',
    nota: 'Los límites que ningún precio puede pasar. Quien puede crear, editar, encender o borrar, también las ve.',
    items: [
      { llave: 'view_pricing_rules', nombre: 'Ver las reglas' },
      { llave: 'create_pricing_rules', nombre: 'Crear reglas', implica: 'view_pricing_rules' },
      {
        llave: 'edit_pricing_rules',
        nombre: 'Editar reglas',
        detalle: 'También quitarles la fecha de fin',
        implica: 'view_pricing_rules',
      },
      {
        llave: 'toggle_pricing_rules',
        nombre: 'Encender y apagar reglas',
        implica: 'view_pricing_rules',
      },
      { llave: 'delete_pricing_rules', nombre: 'Borrar reglas', implica: 'view_pricing_rules' },
    ],
  },
  {
    titulo: 'Administración y análisis',
    items: [
      { llave: 'manage_users', nombre: 'Administrar usuarios' },
      { llave: 'manage_permissions', nombre: 'Administrar roles y permisos' },
      { llave: 'view_analytics', nombre: 'Ver analítica' },
      { llave: 'view_reports', nombre: 'Ver reportes' },
    ],
  },
];

/**
 * Las funcionalidades marcadas después de tocar una casilla, respetando
 * `implica`: marcar una suma la que implica; desmarcar una saca las que la
 * implican. Se exporta para que la prueba mida la misma función que usa el diálogo.
 */
export function alMarcar(
  marcadas: string[],
  llave: string,
  marcado: boolean,
  grupos: GrupoDePermisos[] = GRUPOS_DE_FUNCIONALIDAD
): string[] {
  const items = grupos.flatMap((g) => g.items);
  if (marcado) {
    const implicada = items.find((i) => i.llave === llave)?.implica;
    const suma = [llave, ...(implicada ? [implicada] : [])];
    return [...marcadas, ...suma.filter((l) => !marcadas.includes(l))];
  }
  const laImplican = items.filter((i) => i.implica === llave).map((i) => i.llave);
  return marcadas.filter((l) => l !== llave && !laImplican.includes(l));
}

/**
 * Las marcadas con sus implícitas sumadas: si una marcada tiene `implica`,
 * la implicada también. El diálogo la aplica al abrir: un rol personalizado
 * guardado con «Encender» y sin «Ver» (antes de `implica`, o por la API)
 * llega así, y el servidor igual le da ver. Sin nada que sumar devuelve la
 * MISMA lista, para que el diálogo no avise de más.
 */
export function conImplicadas(
  marcadas: string[],
  grupos: GrupoDePermisos[] = GRUPOS_DE_FUNCIONALIDAD
): string[] {
  const faltan = grupos
    .flatMap((g) => g.items)
    .filter((i) => i.implica && marcadas.includes(i.llave))
    .map((i) => i.implica!)
    .filter((l, idx, todas) => !marcadas.includes(l) && todas.indexOf(l) === idx);
  return faltan.length === 0 ? marcadas : [...marcadas, ...faltan];
}

/**
 * Los grupos que se ofrecen a esta empresa: los de un producto por contrato salen
 * si no lo tiene. Con techo, manda el techo; sin techo, un producto por contrato no
 * se da por omisión (misma regla que `motivoDelBloqueo` y el servidor).
 */
export function gruposDisponibles(
  grupos: GrupoDePermisos[],
  techo: string[] | null
): GrupoDePermisos[] {
  return grupos.filter((g) => {
    if (!g.modulo) return true;
    return techo === null ? !MODULOS_POR_CONTRATO.includes(g.modulo) : techo.includes(g.modulo);
  });
}

/** Todas las llaves declaradas, para el guardián y para el orden de dibujo. */
export function llavesDeclaradas(grupos: GrupoDePermisos[]): string[] {
  return grupos.flatMap((g) => g.items.map((i) => i.llave));
}

/**
 * Lo que el backend declara y acá no está ubicado, y al revés.
 *
 * Es lo que usa la prueba: una llave nueva en el backend tiene que fallar acá
 * y no aparecer suelta al final de la pantalla.
 */
export function llavesSinUbicar(
  delBackend: readonly string[],
  grupos: GrupoDePermisos[]
): { faltan: string[]; sobran: string[]; repetidas: string[] } {
  const declaradas = llavesDeclaradas(grupos);
  const vistas = new Set<string>();
  const repetidas: string[] = [];
  for (const ll of declaradas) {
    if (vistas.has(ll)) repetidas.push(ll);
    vistas.add(ll);
  }
  return {
    faltan: delBackend.filter((ll) => !vistas.has(ll)),
    sobran: declaradas.filter((ll) => !delBackend.includes(ll)),
    repetidas,
  };
}
